import { Button, Input } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import {
  Search,
  X,
  BookOpen,
  ChevronRight,
  ArrowLeft,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Article {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
  content?: string;
}

interface HelpCenterProps {
  organizationSlug: string;
  onStartChat?: () => void;
}

export function HelpCenterPage({ organizationSlug, onStartChat }: HelpCenterProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [organizationSlug]);

  async function fetchData() {
    setLoading(true);

    // Fetch published articles
    const { data: orgData } = await supabase
      .from('organizations')
      .select('id')
      .eq('slug', organizationSlug)
      .single();

    if (!orgData) {
      setLoading(false);
      return;
    }

    const [articlesRes, categoriesRes] = await Promise.all([
      supabase
        .from('document_sources')
        .select('id, title, description, category, created_at, updated_at')
        .eq('organization_id', orgData.id)
        .eq('is_published', true)
        .eq('access_level', 'public')
        .order('display_order', { ascending: true }),
      supabase
        .from('document_sources')
        .select('category')
        .eq('organization_id', orgData.id)
        .eq('is_published', true)
        .eq('access_level', 'public')
        .not('category', 'is', null),
    ]);

    setArticles(articlesRes.data || []);
    const cats = [...new Set((categoriesRes.data || []).map((d) => d.category as string))].sort();
    setCategories(cats);
    setLoading(false);
  }

  async function fetchArticleContent(articleId: string) {
    const { data: chunks } = await supabase
      .from('document_chunks')
      .select('content, chunk_index')
      .eq('source_id', articleId)
      .order('chunk_index', { ascending: true });

    const content = (chunks || []).map((c) => c.content).join('\n\n');
    return content;
  }

  const handleSelectArticle = async (article: Article) => {
    const content = await fetchArticleContent(article.id);
    setSelectedArticle({ ...article, content });
  };

  const filteredArticles = articles.filter((a) => {
    if (selectedCategory && a.category !== selectedCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        a.title.toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Article detail view
  if (selectedArticle) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost"
          onClick={() => setSelectedArticle(null)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to articles
        </Button>

        <article>
          {selectedArticle.category && (
            <span className="text-xs font-medium text-link uppercase tracking-wider">
              {selectedArticle.category}
            </span>
          )}
          <h1 className="support-page-title">
            {selectedArticle.title}
          </h1>
          {selectedArticle.description && (
            <p className="text-lg text-muted-foreground mb-6">{selectedArticle.description}</p>
          )}
          <div className="text-xs text-muted-foreground mb-8">
            Last updated: {new Date(selectedArticle.updated_at).toLocaleDateString()}
          </div>
          <div className="prose prose-gray max-w-none">
            {(selectedArticle.content || '').split('\n').map((paragraph, i) => (
              <p key={i} className="mb-4 text-foreground leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </article>

        {/* CTA */}
        {onStartChat && (
          <div className="mt-12 p-6 bg-muted rounded-xl border border-border text-center">
            <p className="text-foreground font-medium mb-2">Still need help?</p>
            <Button variant="ghost"
              onClick={onStartChat}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors font-medium"
            >
              <MessageSquare className="w-5 h-5" />
              Start a conversation
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Main help center view
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="support-page-title">How can we help?</h1>
        <p className="text-lg text-muted-foreground">Search our knowledge base or browse by category</p>
      </div>

      {/* Search */}
      <div className="relative max-w-xl mx-auto mb-10">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search articles..."
          className="w-full pl-12 pr-10 py-3 border border-border rounded-xl text-base focus:ring-2 focus:ring-ring focus:border-primary shadow-sm"
        />
        {searchQuery && (
          <Button aria-label="Clear search" size="iconSm" variant="ghost"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-link" />
        </div>
      ) : (
        <>
          {/* Categories */}
          {!searchQuery && categories.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <Button variant="ghost"
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-border'
                }`}
              >
                All
              </Button>
              {categories.map((cat) => (
                <Button variant="ghost"
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-border'
                  }`}
                >
                  {cat}
                </Button>
              ))}
            </div>
          )}

          {/* Articles */}
          {filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No articles found</p>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredArticles.map((article) => (
                <Button  variant="ghost"
                  key={article.id}
                  onClick={() => handleSelectArticle(article)}
                  className="h-auto whitespace-normal justify-start w-full text-left bg-card rounded-lg border border-border p-5 hover:border-primary hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {article.category && (
                        <span className="text-[10px] font-semibold text-link uppercase tracking-wider">
                          {article.category}
                        </span>
                      )}
                      <h3 className="font-semibold text-foreground group-hover:text-link transition-colors">
                        {article.title}
                      </h3>
                      {article.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{article.description}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-link flex-shrink-0 ml-4 transition-colors" />
                  </div>
                </Button>
              ))}
            </div>
          )}

          {/* Start a conversation CTA */}
          {onStartChat && (
            <div className="mt-12 p-6 bg-muted rounded-xl border border-primary text-center">
              <p className="text-foreground font-medium mb-2">Can't find what you're looking for?</p>
              <Button variant="ghost"
                onClick={onStartChat}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors font-medium"
              >
                <MessageSquare className="w-5 h-5" />
                Start a conversation
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
